import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { get, useAppDispatch } from '@/utils';
import { tablesActions } from '@/store/tables/slice';
import { clearErrorLog } from '@/providers/error-boundary/error-bus';
import { AuthContext } from './context';
import { AuthState } from './types';
import type { IAuthRole, IUser } from './types';
import keycloak from './keycloak';
import { ACTIVE_REFRESH_WINDOW_MS, isUserActive, markActivity } from './activity';
import { SessionExpiredModal } from './SessionExpiredModal';
import { moderatorPermissionKeys, readModeratedNeighborhood } from './neighborhood-moderation';

const ROLE_MAP: Record<string, IUser['role']> = {
	superadmin: 'superadmin',
	admin: 'admin',
	// ambele roluri de administrație (BE: Roles.cs) sunt content-manageri în aplicație;
	// „User" (cetățean mobil) rămâne intenționat nemapat → NoAccess
	'administration user': 'fsp',
	'administration admin': 'fsp',
	moderator: 'moderator',
	comunicator: 'moderator',
	extern: 'fsp',
	fsp: 'fsp'
};

// Flatten the permission keys from all of the user's roles (api/users/me returns
// roles[].permissions[].key). Deduped so a key shared across roles appears once.
const extractPermissionKeys = (raw: any): string[] => {
	const roles = Array.isArray(raw?.roles) ? raw.roles : [];
	const keys = roles
		.flatMap((r: any) => (Array.isArray(r?.permissions) ? r.permissions : []))
		.map((p: any) => (typeof p === 'string' ? p : p?.key))
		.filter((k: any): k is string => typeof k === 'string' && k.length > 0);
	return Array.from(new Set<string>(keys));
};

const getRoleName = (raw: any): string | undefined => {
	const singleRole = raw?.role;
	if (singleRole && typeof singleRole === 'object' && 'name' in singleRole) {
		return singleRole.name;
	}
	if (typeof singleRole === 'string') {
		return singleRole;
	}

	const firstRole = Array.isArray(raw?.roles) ? raw.roles[0] : undefined;
	if (firstRole && typeof firstRole === 'object' && 'name' in firstRole) {
		return firstRole.name;
	}
	if (typeof firstRole === 'string') {
		return firstRole;
	}

	return undefined;
};

const initialUser: IUser = {
	id: 0,
	firstName: '',
	lastName: '',
	email: '',
	role: undefined,
	isLoggedIn: false
};

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [user, setUser] = useState<IUser | undefined>(initialUser);
	const [availableRoles, setAvailableRoles] = useState<IAuthRole[]>([]);
	const [authState, setAuthState] = useState<AuthState>(AuthState.Loading);
	// Sesiune expirată (inactivitate / refresh eșuat / 401) → overlay cu modala de expirare.
	const [sessionExpired, setSessionExpired] = useState(false);
	// Rolurile se încarcă LAZY (la cerere), NU la bootstrap. Guard idempotent — un singur GET api/roles.
	const rolesLoadedRef = useRef(false);
	const rolesLoadingRef = useRef(false);
	const dispatch = useAppDispatch();

	const applySignedOut = useCallback(() => {
		setUser(initialUser);
		setAuthState(AuthState.SignedOut);
		// Rolurile sunt per sesiune → se golesc și se re-încarcă lazy la următorul login.
		setAvailableRoles([]);
		rolesLoadedRef.current = false;
		rolesLoadingRef.current = false;
		// Configurațiile de coloane ale tabelelor sunt per utilizator → se golesc la logout.
		dispatch(tablesActions.resetTablesConfig());
		// La fel și log-ul de erori al sesiunii (CTZ-2758) — e per sesiune de utilizator.
		clearErrorLog();
	}, [dispatch]);

	// Încărcare lazy a listei de roluri (folosită doar în UI-ul care chiar are nevoie de ele:
	// dropdown-uri de rol, tab-uri de utilizatori, RBAC). Nu se cere la bootstrap, deci userii
	// fără `roles:read` nu mai depind de un GET care oricum le-ar da 403.
	const loadAvailableRoles = useCallback(() => {
		if (rolesLoadedRef.current || rolesLoadingRef.current) return;
		rolesLoadingRef.current = true;
		get('api/roles')
			.then((res) => {
				const payload = res?.data;
				const roles = Array.isArray(payload)
					? payload
					: Array.isArray(payload?.roles)
						? payload.roles
						: [];
				setAvailableRoles(roles as IAuthRole[]);
				rolesLoadedRef.current = true;
			})
			.catch(() => {
				// fără roles:read → 403; lista rămâne goală, nu blocăm nimic
			})
			.finally(() => {
				rolesLoadingRef.current = false;
			});
	}, []);

	// Marchează sesiunea ca expirată → modala. Idempotent (401-uri multiple în zbor sunt ok).
	const expireSession = useCallback(() => {
		setSessionExpired(true);
	}, []);

	// Refs so bootstrap ([] deps) and intervals can always call the latest version.
	const applySignedOutRef = useRef(applySignedOut);
	useEffect(() => {
		applySignedOutRef.current = applySignedOut;
	}, [applySignedOut]);
	const expireSessionRef = useRef(expireSession);
	useEffect(() => {
		expireSessionRef.current = expireSession;
	}, [expireSession]);
	const authStateRef = useRef(authState);
	useEffect(() => {
		authStateRef.current = authState;
	}, [authState]);

	// Activitatea reală a utilizatorului (throttle 1s) — decide dacă mai reînnoim tokenul.
	useEffect(() => {
		const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
		let last = 0;
		const onActivity = (): void => {
			const now = Date.now();
			if (now - last < 1000) return;
			last = now;
			markActivity();
		};
		events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
		return () => events.forEach((e) => window.removeEventListener(e, onActivity));
	}, []);

	// Token-expiry: dacă userul e ACTIV → reînnoim transparent; dacă e INACTIV → lăsăm
	// sesiunea să expire (modala), nu o mai ținem vie. Ref-uri → nu re-înregistrăm la render.
	useEffect(() => {
		keycloak.onTokenExpired = () => {
			if (isUserActive(ACTIVE_REFRESH_WINDOW_MS)) {
				keycloak.updateToken(30).catch(() => expireSessionRef.current());
			} else {
				expireSessionRef.current();
			}
		};
	}, []);

	// 401 în zbor (interceptorul axios emite `unauthenticated`) → sesiune expirată, DOAR
	// dacă chiar aveam sesiune (evită modala pe bootstrap/pagina de login).
	useEffect(() => {
		const onUnauthenticated = (): void => {
			if (authStateRef.current === AuthState.SignedIn) expireSessionRef.current();
		};
		window.addEventListener('unauthenticated', onUnauthenticated);
		return () => window.removeEventListener('unauthenticated', onUnauthenticated);
	}, []);

	// keycloak.init() must be called exactly once per page load — the ref guard
	// prevents re-runs when applySignedOut ref changes after bootstrap.
	const bootstrapped = useRef(false);
	useEffect(() => {
		if (bootstrapped.current) return undefined;
		bootstrapped.current = true;

		let mounted = true;

		const bootstrap = async () => {
			try {
				// check-sso (nu login-required): utilizatorii cu sesiune SSO activă sunt
				// autentificați silențios (iframe → silent-check-sso.html), iar cei fără
				// sesiune ajung pe pagina NOASTRĂ de login (/auth), nu direct pe Keycloak
				const authenticated = await keycloak.init({
					onLoad: 'check-sso',
					silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
					pkceMethod: 'S256',
					checkLoginIframe: false
				});

				if (!mounted) return;

				if (!authenticated) {
					applySignedOutRef.current();
					return;
				}

				// Bootstrap-ul cere DOAR profilul + permisiunile userului logat (api/users/me).
				// Lista de roluri (api/roles) se încarcă lazy, doar unde e nevoie (loadAvailableRoles).
				const response = await get('api/users/me');
				if (!mounted) return;

				const raw = response.data?.user ?? response.data;
				const roleName = getRoleName(raw);
				const staffRole = ROLE_MAP[typeof roleName === 'string' ? roleName.toLowerCase() : ''];
				// Un cetățean fără rol de staff intră doar ca moderator al cartierului lui — și
				// doar dacă are măcar un drept acolo; altfel rămâne NoAccess.
				const moderatedNeighborhood = staffRole ? null : readModeratedNeighborhood(raw);
				const role = staffRole ?? (moderatedNeighborhood ? 'neighborhoodModerator' : undefined);
				const permissions = extractPermissionKeys(raw);
				const profile: IUser = {
					...raw,
					id:
						raw?.id === undefined || raw?.id === null || raw?.id === ''
							? 0
							: Number(raw.id),
					role,
					moderatedNeighborhood: moderatedNeighborhood ?? undefined,
					permissions: moderatedNeighborhood
						? Array.from(
								new Set([...permissions, ...moderatorPermissionKeys(moderatedNeighborhood)])
							)
						: permissions,
					countryCode: raw?.countryCode || raw?.phoneCountryCode || undefined,
					isLoggedIn: true
				};

				if (!profile.role) {
					setAuthState(AuthState.NoAccess);
					return;
				}
				setUser(profile);
				setAuthState(AuthState.SignedIn);
			} catch (err: any) {
				if (!mounted) return;
				const status = err?.response?.status;
				if (status === 401 || status === 403 || status === 404) {
					setAuthState(AuthState.NoAccess);
				} else {
					applySignedOutRef.current();
				}
			}
		};

		bootstrap();
		return () => {
			mounted = false;
		};
	}, []);

	// Reînnoire proactivă la fiecare 30s — DOAR cât timp userul a fost activ recent.
	// Inactiv → nu reînnoim, lăsăm sesiunea să expire (securitate: fără keepalive infinit).
	useEffect(() => {
		if (authState !== AuthState.SignedIn) return undefined;
		const id = setInterval(() => {
			if (!isUserActive(ACTIVE_REFRESH_WINDOW_MS)) return;
			keycloak.updateToken(70).catch(() => expireSessionRef.current());
		}, 30_000);
		return () => clearInterval(id);
	}, [authState]);

	const signIn = useCallback(() => {
		keycloak.login({ redirectUri: `${window.location.origin}/auth/callback` });
	}, []);

	// CTA-ul modalei de expirare: curăță tokenele + ruta salvată și pornește re-autentificarea
	// (fresh login prin Keycloak). Modala se închide odată cu redirectul.
	const returnToLogin = useCallback(() => {
		setSessionExpired(false);
		localStorage.removeItem('currentPath');
		try {
			keycloak.clearToken();
		} catch {
			/* fără token de curățat */
		}
		signIn();
	}, [signIn]);

	const signOut = useCallback(async () => {
		applySignedOut();
		// ruta salvată e per sesiune de utilizator — nu o restaurăm după re-login
		localStorage.removeItem('currentPath');
		try {
			// NOTĂ (REV-AUTH-01): redirectUri devine post_logout_redirect_uri și TREBUIE
			// înregistrat în clientul Keycloak (cityzen-fe → „Valid post logout redirect
			// URIs", ex. https://instruire.cityzen.ro/*). Fără el Keycloak afișează
			// „Invalid redirect uri" și sesiunea SSO rămâne activă.
			await keycloak.logout({ redirectUri: `${window.location.origin}/auth` });
		} catch {
			// dacă redirectul de logout eșuează, curățăm tokenele locale și forțăm
			// întoarcerea pe pagina de autentificare — rutele securizate rămân blocate
			keycloak.clearToken();
			window.location.replace(`${window.location.origin}/auth`);
		}
	}, [applySignedOut]);

	const refreshAuthState = useCallback(
		(next: AuthState) => {
			if (next === AuthState.SignedOut) {
				applySignedOut();
			} else {
				setAuthState(next);
			}
		},
		[applySignedOut]
	);

	const value = useMemo(
		() => ({
			user,
			setUser,
			signIn,
			signOut,
			refreshAuthState,
			authState,
			availableRoles,
			loadAvailableRoles,
			sessionExpired,
			returnToLogin
		}),
		[
			user,
			signIn,
			signOut,
			authState,
			refreshAuthState,
			availableRoles,
			loadAvailableRoles,
			sessionExpired,
			returnToLogin
		]
	);

	return (
		<AuthContext.Provider value={value}>
			{children}
			{/* Overlay global: modala de expirare peste orice rută/stare, tematizată. */}
			<SessionExpiredModal open={sessionExpired} onReLogin={returnToLogin} />
		</AuthContext.Provider>
	);
};
